import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from 'leaflet'; 
import "leaflet/dist/leaflet.css";

/**
 * A responsive map component that displays devices on a map.
 * The data passed to the component should contain information about each device's
 * name, latitude (lat), and longitude (lng).
 * 
 * @param {Array} data - An array of device objects with `name`, `lat`, and `lng`
 * 
 * @returns {JSX.Element} The rendered map with markers for each device.
 * 
 * @example
 * const data = [
 *   { id: "1", name: "Device 1", lat: 37.7749, lng: -122.4194 },
 *   { id: "2", name: "Device 2", lat: 51.505, lng: -0.09 }
 * ];
 * <WidgetMap data={data} />
 */
const WidgetMap = ({ data , zoomLevelRef, isNewData  }) => {
  const [deviceLocations, setDeviceLocations] = useState([]);

  const defaultIcon = new L.Icon({
    iconUrl: require('leaflet/dist/images/marker-icon.png'), // Default icon
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'), // Retina icon
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'), // Shadow for the marker
    iconSize: [25, 41], // Standard size for the marker icon
    iconAnchor: [12, 41], // Anchor point for the icon
    popupAnchor: [0, -41], // Popup anchor point relative to the icon
  });

  const [zoomLevel, setZoomLevel] = useState(zoomLevelRef.current);
  const [newRender, setNewRender] = useState(false);

  useEffect(() => {
    if (data) {
      // If `data` is a single object, wrap it in an array
      // Leaving with array because we may want to do route with multiple devices later
      const locations = Array.isArray(data) ? data : [data];
      // Filter out devices without valid lat/lng
      const validLocations = locations.filter(device => device.lat && device.lng);
      setDeviceLocations(validLocations);
    }
  }, [data]);

    useEffect(() => {
      if (isNewData) {
        // Force re-render if new data is received
        setNewRender(true);
      }
    }, [isNewData]);

  // This hook will ensure the map zooms to fit the bounds of the markers
  const MapZoom = () => {
    const map = useMap();
    useEffect(() => {
      if (deviceLocations.length > 0) {
        // is new render and zoom is never set, auto zoom to fit all markers
        if (newRender) {
          const bounds = deviceLocations.map(device => [device.lat, device.lng]);
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14, minZoom: 2 });  // Automatically adjust zoom to fit all markers
          zoomLevelRef.current = map.getZoom();
        } else {
          map.setZoom(zoomLevelRef.current); // Set zoom level from ref
        }
      }

      map.on('zoomend', () => {
        zoomLevelRef.current = map.getZoom();
        // set the zoom level to force re-rendering of the map
        setZoomLevel(zoomLevelRef.current);
        // no new rendering needed
        setNewRender(false);
      });

      // Cleanup function to remove the event listener
      return () => {
         // Remove the zoom event listener when component unmounts or effect changes
        map.off('zoomend');
      };

    }, [newRender]);

    return null;
  };
  const center = deviceLocations.length > 0 ? [deviceLocations[0].lat, deviceLocations[0].lng] : [0, 0];

  return (
    <div>     
    <MapContainer center={center} zoom={zoomLevel} style={{ height: "300px", width: "100%" }}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <MapZoom />
      {deviceLocations.length === 0 && <div>No devices available</div>}
      {deviceLocations.map((device) => (
        <Marker 
          key={device.id} 
          position={[device.lat, device.lng]}
          icon={defaultIcon} 
          >
          <Popup>
            <b>{device.name}</b>
            <br />
            Lat: {device.lat}, Lng: {device.lng}
          </Popup>
        </Marker>
      ))}
    </MapContainer>
    </div>
  );
};

export default WidgetMap;
